import { trackMiscAction } from 'analytics';
import Icon, { IconType } from 'components/Icon';
import ShareFrameModal from 'components/Modal/Share/ShareFrameModal';
import InspectorLeftPanel from 'components/Panel/InspectorLeftPanel';
import Button, { ButtonType } from 'components/Widget/Button/Button';
import { Tooltip } from 'components/Widget/Tooltip';
import { DEFAULT_DEVICES, Device, DomElement, FrameType, SelectionInfo } from 'dragonmouse-shared/interfaces';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { selectActiveScene, selectFrame } from 'redux/db/reducers';
import { setPrototypeScaling, setSelectedDomElements } from 'redux/mode/actions';
import { RootReducer } from 'redux/reducers';
import * as routes from 'routes';
import { useTheme } from 'styled-components';
import { layout } from 'tokens';
import { frameSiteUrl } from 'utils';
import Prototype from './Prototype';
import { getSelectionSvg, getAdjacentFrame, Direction } from 'redux/db/util';
import { toast } from 'react-toastify';
import * as S from './styles';
import { Point } from 'dragonmouse-shared/geometry';

const { inspectorNavbarHeight: navbarHeight } = layout;

interface FrameInspectorProps {
	frameId: string;
}

const FrameInspector = ({ frameId }: FrameInspectorProps) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [showShareModal, setShowShareModal] = useState(false);
	const [panelsVisible, setPanelsVisible] = useState(true);
	const theme = useTheme();
	const [selectMode, setSelectMode] = useState(false);
	const [iframeReady, setIframeReady] = useState(false);
	const [contextMenuOpen, setContextMenuOpen] = useState(false);
	const [selectionInfo, setSelectionInfo] = useState<SelectionInfo | null>(null);
	const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(
		null
	);
	const [fullscreenMode, setFullscreenMode] = useState(false);
	const [prototypeFrameSize, setPrototypeFrameSize] = useState({ width: 0, height: 0 });

	const { scene, frame, prototypeScaling, isDragging } = useSelector((r: RootReducer) => {
		return {
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			frame: selectFrame(r.db, frameId),
			prototypeScaling: r.mode.prototypeScaling,
			isDragging: r.mode.isDragging,
		};
	});
	const siteUrl = frame ? frameSiteUrl(frame) : null;

	//------------------------------------------------------------------------------------------------
	// UTILITY
	//------------------------------------------------------------------------------------------------

	const exit = () => {
		if (scene) {
			navigate(routes.scene(scene.id, scene.title));
		} else {
			console.error('FrameInspector: cannot access scene');
		}
	};

	const openInNewTab = () => {
		if (siteUrl) window.open(siteUrl, '_blank', 'noopener,noreferrer');
	};

	const handleDevicePreset = (device: Device) => {
		setPrototypeFrameSize({ width: device.width, height: device.height });
	};

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	useEffect(() => {
		// here's a cleanup function to reset selected element ids when component unmounts
		return () => {
			dispatch(setSelectedDomElements([]));
		};
	}, [dispatch]);

	// Initialize prototype frame size when frame changes
	useEffect(() => {
		if (frame) {
			setPrototypeFrameSize({ width: frame.width, height: frame.height });
		}
	}, [frame?.width, frame?.height]);

	const onSelectElement = (element: DomElement) => {
		dispatch(setSelectedDomElements([element]));
	};

	//------------------------------------------------------------------------------------------------
	// NAVIGATION LOGIC
	//------------------------------------------------------------------------------------------------
	const navigateToFrame = (targetFrameId: string | null) => {
		if (targetFrameId && scene) {
			navigate(routes.frame(scene.id, scene.title, targetFrameId));
		}
	};

	const onArrowLeft = () => {
		if (frame?.id) {
			const targetFrame = getAdjacentFrame(frame.id, Direction.LEFT);
			navigateToFrame(targetFrame?.id || null);
		}
	};

	const onArrowRight = () => {
		if (frame?.id) {
			const targetFrame = getAdjacentFrame(frame.id, Direction.RIGHT);
			navigateToFrame(targetFrame?.id || null);
		}
	};

	const onArrowUp = () => {
		if (frame?.id) {
			const targetFrame = getAdjacentFrame(frame.id, Direction.UP);
			navigateToFrame(targetFrame?.id || null);
		}
	};

	const onArrowDown = () => {
		if (frame?.id) {
			const targetFrame = getAdjacentFrame(frame.id, Direction.DOWN);
			navigateToFrame(targetFrame?.id || null);
		}
	};

	// Check if navigation is available in each direction
	const canNavigateLeft = frame?.id ? !!getAdjacentFrame(frame.id, Direction.LEFT) : false;
	const canNavigateRight = frame?.id ? !!getAdjacentFrame(frame.id, Direction.RIGHT) : false;
	const canNavigateUp = frame?.id ? !!getAdjacentFrame(frame.id, Direction.UP) : false;
	const canNavigateDown = frame?.id ? !!getAdjacentFrame(frame.id, Direction.DOWN) : false;

	// Handle global Escape key to clear selection and close context menu
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			const target = e.target as HTMLElement;
			const consumesArrowKeys =
				target.tagName === 'INPUT' ||
				target.tagName === 'TEXTAREA' ||
				target.isContentEditable ||
				target.closest('input, textarea, [contenteditable="true"]');

			if (e.key === 'Escape') {
				setSelectMode(false);
				dispatch(setSelectedDomElements([]));
				setContextMenuOpen(false);
				setSelectionInfo(null);
			} else if (!consumesArrowKeys) {
				// Only handle arrow keys when not in an editable element
				if (e.key === 'ArrowLeft' && canNavigateLeft) {
					e.preventDefault();
					onArrowLeft();
				} else if (e.key === 'ArrowRight' && canNavigateRight) {
					e.preventDefault();
					onArrowRight();
				} else if (e.key === 'ArrowUp' && canNavigateUp) {
					e.preventDefault();
					onArrowUp();
				} else if (e.key === 'ArrowDown' && canNavigateDown) {
					e.preventDefault();
					onArrowDown();
				}
			}
		};

		// the third parameter here ('true') seems needed to capture all keyboard events
		window.addEventListener('keydown', handleKeyDown, true);
		return () => window.removeEventListener('keydown', handleKeyDown, true);
	}, [
		canNavigateLeft,
		canNavigateRight,
		canNavigateUp,
		canNavigateDown,
		onArrowLeft,
		onArrowRight,
		onArrowUp,
		onArrowDown,
		dispatch,
	]);

	//------------------------------------------------------------------------------------------------
	// EARLY EXIT (must be defined after all hooks)
	//------------------------------------------------------------------------------------------------

	if (!frame) return null;

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onContextMenu = (selectionInfo: SelectionInfo, point: Point) => {
		if (!frame) return;
		// Use the position data that's already calculated in the Prototype component
		setContextMenuPosition(point);
		setSelectionInfo(selectionInfo);
		setContextMenuOpen(true);
	};

	const handleExportClick = async () => {
		if (!selectionInfo || !frame?.siteId) return;

		try {
			const svgBlob = await getSelectionSvg(selectionInfo, frame.siteId);
			if (svgBlob) {
				const svgText = await svgBlob.text();
				await navigator.clipboard.writeText(svgText);
				toast.success('SVG copied to clipboard');
			}
		} catch (error) {
			toast.error('Failed to copy SVG');
		}
		setContextMenuOpen(false);
		setSelectionInfo(null);
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const backgroundColor = frame.metadata?.type === FrameType.PROTOTYPE ? '#391252' : theme.gray[0];
	return (
		<S.Base panelsVisible={panelsVisible}>
			{/*TODO -- decide if we need this.  I think this is left over from v1.  Research and decide before merging
			 {imageUrl && <S.ImagePreloader src={imageUrl} />} */}
			<S.Navbar height={navbarHeight} backgroundColor={backgroundColor}>
				<S.NavbarLeft>
					{scene && (
						<S.NavbarButton onClick={exit}>
							<Icon type={IconType.LINE_ARROW_LEFT} width={16} height={16} />
						</S.NavbarButton>
					)}
					<Tooltip placement="bottom" id="side-panel-tooltip" />
					<S.NavbarButton
						onClick={() => {
							panelsVisible
								? trackMiscAction('close_inspector_side_panel')
								: trackMiscAction('open_inspector_side_panel');
							setPanelsVisible(!panelsVisible);
						}}
						data-tooltip-id="side-panel-tooltip"
						data-tooltip-content={panelsVisible ? 'Close side panel' : 'Open side panel'}
					>
						<Icon
							type={panelsVisible ? IconType.LEFT_PANE_VISIBLE : IconType.LEFT_PANE_HIDDEN}
							width={16}
							height={16}
						/>
					</S.NavbarButton>
					<S.Title>{frame.title}</S.Title>
				</S.NavbarLeft>

				<S.NavbarCenter>
					<Tooltip placement="bottom" id="select-tooltip" />
					<S.SelectButton
						active={selectMode}
						disabled={!iframeReady || !siteUrl}
						onClick={() => {
							setSelectMode(!selectMode);
							dispatch(setSelectedDomElements([]));
						}}
						data-tooltip-id="select-tooltip"
						data-tooltip-content={
							!siteUrl
								? 'Selection mode is not available for imported images'
								: iframeReady
								? 'Activate selection mode (click on frame elements to add them to chat)'
								: 'Prototype code is loading, please wait...'
						}
					>
						<Icon type={IconType.SELECT} width={16} height={16} />
						Select
					</S.SelectButton>
				</S.NavbarCenter>

				<S.NavbarRight>
					{/* Device Preset Buttons */}
					{DEFAULT_DEVICES.map((device) => {
						const isActive =
							prototypeFrameSize.width === device.width &&
							prototypeFrameSize.height === device.height;
						const tooltipId = `device-${device.type}-tooltip`;

						return (
							<div key={device.type}>
								<Tooltip placement="bottom" id={tooltipId} />
								<S.NavbarButton
									active={isActive}
									disabled={!siteUrl}
									onClick={() => handleDevicePreset(device)}
									data-tooltip-id={tooltipId}
									data-tooltip-content={
										!siteUrl
											? 'Device presets are not available for imported images'
											: `Set to ${device.type} (${device.width}×${device.height})`
									}
								>
									<Icon
										type={
											device.type === 'mobile'
												? IconType.MOBILE
												: device.type === 'tablet'
												? IconType.TABLET
												: IconType.DESKTOP
										}
										width={16}
										height={16}
									/>
								</S.NavbarButton>
							</div>
						);
					})}

					{/* Divider */}
					<S.Divider />

					<Tooltip placement="bottom" id="fullscreen-tooltip" />
					<S.NavbarButton
						active={fullscreenMode}
						disabled={!siteUrl}
						onClick={() => setFullscreenMode(!fullscreenMode)}
						data-tooltip-id="fullscreen-tooltip"
						data-tooltip-content={
							!siteUrl
								? 'Fullscreen view is not available for imported images'
								: fullscreenMode
								? 'Switch to fixed aspect ratio view'
								: 'Switch to fullscreen view'
						}
					>
						<Icon type={IconType.FULLSCREEN} width={16} height={16} />
					</S.NavbarButton>
					<Tooltip placement="bottom" id="scaling-tooltip" />
					<S.NavbarButton
						active={prototypeScaling === 'actual-size'}
						onClick={() =>
							dispatch(
								setPrototypeScaling(prototypeScaling === 'actual-size' ? 'mockup' : 'actual-size')
							)
						}
						data-tooltip-id="scaling-tooltip"
						data-tooltip-content={
							prototypeScaling === 'actual-size'
								? 'Switch to mockup view'
								: 'Switch to actual size view'
						}
					>
						<Icon type={IconType.ACTUAL_SIZE} width={16} height={16} />
					</S.NavbarButton>
					<Tooltip placement="bottom" id="open-external-tooltip" />
					<S.NavbarButton
						onClick={openInNewTab}
						disabled={!siteUrl}
						data-tooltip-id="open-external-tooltip"
						data-tooltip-content="Open in new tab"
					>
						<Icon type={IconType.OPEN_EXTERNAL} width={16} height={16} />
					</S.NavbarButton>
					<S.Share>
						<Button
							onSelected={() => {
								trackMiscAction('open_frame_share_modal');
								setShowShareModal(true);
							}}
							type={ButtonType.NOTICEABLE}
						>
							Share
						</Button>
					</S.Share>
				</S.NavbarRight>
			</S.Navbar>
			<S.PrototypeArea>
				<Prototype
					frame={frame}
					siteUrl={siteUrl}
					isDragging={isDragging}
					selectMode={selectMode}
					fullscreenMode={fullscreenMode}
					onSelectElement={onSelectElement}
					onSetSelectMode={setSelectMode}
					onSetSelectedElements={(elements) => dispatch(setSelectedDomElements(elements))}
					onIframeReadyChange={setIframeReady}
					onContextMenu={onContextMenu}
					prototypeFrameSize={prototypeFrameSize}
					onSetFrameSize={setPrototypeFrameSize}
				/>
			</S.PrototypeArea>
			<S.Footer>
				<S.FooterActions>
					<S.FooterButton
						onClick={onArrowLeft}
						disabled={!canNavigateLeft}
						title="Navigate left (left arrow key)"
					>
						<Icon type={IconType.LINE_ARROW_LEFT} width={11} height={11} />
					</S.FooterButton>
					<S.FooterButton
						onClick={onArrowRight}
						disabled={!canNavigateRight}
						title="Navigate right (right arrow key)"
					>
						<Icon type={IconType.LINE_ARROW_RIGHT} width={11} height={11} />
					</S.FooterButton>
					<S.FooterSpacer />
					<S.FooterButton
						onClick={onArrowUp}
						disabled={!canNavigateUp}
						title="Navigate up (up arrow key)"
					>
						<Icon type={IconType.LINE_ARROW_UP} width={11} height={11} />
					</S.FooterButton>
					<S.FooterButton
						onClick={onArrowDown}
						disabled={!canNavigateDown}
						title="Navigate down (down arrow key)"
					>
						<Icon type={IconType.LINE_ARROW_DOWN} width={11} height={11} />
					</S.FooterButton>
				</S.FooterActions>
				<S.DimensionsDisplay>
					Frame size: {Math.round(prototypeFrameSize.width)} ×{' '}
					{Math.round(prototypeFrameSize.height)}
				</S.DimensionsDisplay>
			</S.Footer>
			{panelsVisible && (
				<InspectorLeftPanel frameId={frameId} prototypeWidth={prototypeFrameSize.width} />
			)}
			{showShareModal && (
				<ShareFrameModal frameId={frame.id} onClose={() => setShowShareModal(false)} />
			)}
			{contextMenuOpen && contextMenuPosition && (
				<S.ContextMenuWrapper x={contextMenuPosition.x} y={contextMenuPosition.y} isVisible={true}>
					<S.ContextMenu>
						<S.Items onClick={handleExportClick}>Copy selection</S.Items>
					</S.ContextMenu>
				</S.ContextMenuWrapper>
			)}
		</S.Base>
	);
};

export default FrameInspector;
